<template>
  <ion-card class="time-report-card">
    <ion-card-content>
      <ion-row class="card-header">
        <ion-label class="card-title" color="dark">Suivi de journée</ion-label>
        <ion-icon
          :id="'help-popover-trigger'"
          :icon="helpCircleOutline"
          color="dark"
          class="help-icon"
        ></ion-icon>
      </ion-row>

      <ion-popover trigger="help-popover-trigger">
        <ion-content class="ion-padding">
          <p>
            Le suivi de journée permet de définir des
            <strong>périodes de travail</strong>.
          </p>
          <p>
            Ce sont ces périodes qui remplissent les
            <strong>heures travaillées</strong> pour la RH.
          </p>
          <p>
            La <strong>durée minimale</strong> d'une période de travail est de
            <strong>5 minutes</strong>
          </p>
        </ion-content>
      </ion-popover>

      <ion-row class="ion-align-items-center">
        <ion-button
          class="start-stop-btn"
          :class="{ active: !!start }"
          :disabled="(start && !isOnline) || isSaving || existingTimersLoading"
          @click="toggleTimer"
        >
          <span>
            <ion-icon
              :icon="start ? stopCircleOutline : alarmOutline"
            ></ion-icon>

            <div>{{ isSaving ? '...' : start ? 'Arrêter' : 'Démarrer' }}</div>
          </span>
        </ion-button>

        <div class="timer-display">
          <div class="timer">
            <strong v-if="start">En cours</strong>
            <span style="opacity: 0.6" v-else>En attente...</span>
          </div>

          <div v-if="formattedStart" class="start-time">
            Commencé à {{ formattedStart }}
          </div>
        </div>
      </ion-row>

      <ion-row v-if="existingTimers.length > 0">
        <ion-button
          fill="clear"
          size="small"
          @click="showHistory = !showHistory"
          class="toggle-btn"
        >
          <ion-icon
            :icon="showHistory ? chevronUpOutline : chevronDownOutline"
          ></ion-icon>

          <span>Historique ({{ existingTimers.length }})</span>
        </ion-button>
      </ion-row>

      <div v-if="showHistory && existingTimers.length > 0" class="timers-list">
        <div
          v-for="(timer, index) in existingTimers"
          :key="'timer-' + index"
          class="timer-item"
        >
          <div class="timer-time">
            {{ floatToString(timer.dayStart) }} -
            {{ floatToString(timer.dayEnd) }}
          </div>

          <strong class="timer-duration">
            {{ floatToString(timer.day, 'h').replace(/^0/, '') }}
          </strong>
        </div>
      </div>
    </ion-card-content>
  </ion-card>
</template>

<script>
import dayjs from 'dayjs';
import {
  IonCard,
  IonCardContent,
  IonButton,
  IonLabel,
  IonIcon,
  IonRow,
  IonPopover,
  IonContent,
  alertController,
  toastController,
} from '@ionic/vue';
import {
  helpCircleOutline,
  alarmOutline,
  stopCircleOutline,
  chevronUpOutline,
  chevronDownOutline,
} from 'ionicons/icons';
import { getParams } from '@/services/users';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { roundToFiveMinutes, floatToString } from '@/services/utils/dateUtils';
import { setItemSafe } from '@/services/utils/localStorageUtils';

// Magic constants
const HOUR_IN_SECONDS = 3600;
const MINUTE_IN_SECONDS = 60;
const TIMER_INTERVAL = 1000;
const PRECISION = 4;
const TIMER_STORAGE_KEY = 'timeReportTimer_current';

export default {
  name: 'TimeReportTimer',

  components: {
    IonCard,
    IonCardContent,
    IonButton,
    IonLabel,
    IonIcon,
    IonRow,
    IonPopover,
    IonContent,
  },

  setup() {
    return {
      helpCircleOutline,
      alarmOutline,
      stopCircleOutline,
      chevronUpOutline,
      chevronDownOutline,
      floatToString,
    };
  },

  data() {
    return {
      start: null,
      timerInterval: null,
      syncInterval: null,
      // seconds elapsed since start
      timeElapsed: 0,
      showHistory: false,
      isOnline: navigator.onLine,
      onlineHandler: null,
      offlineHandler: null,
      visibilityHandler: null,
      isSaving: false,

      // fetched
      existingTimers: [],
      existingTimersLoading: false,
      hoursTotal: 0,
    };
  },

  computed: {
    /**
     * Unbelievable but true, seems drivers are scared of the timer running...
     * So we have been asked to remove it.
    formattedTime() {
      if (!this.start) return '00:00:00';

      return [
        Math.floor(this.timeElapsed / HOUR_IN_SECONDS),
        Math.floor((this.timeElapsed % HOUR_IN_SECONDS) / MINUTE_IN_SECONDS),
        this.timeElapsed % MINUTE_IN_SECONDS,
      ]
        .map(u => u.toString().padStart(2, '0'))
        .join(':');
    },
    */

    formattedStart() {
      return this.start ? this.start.format('HH:mm') : null;
    },
  },

  mounted() {
    this.loadExistingTimers();
    this.restoreTimerFromStorage();

    this.onlineHandler = () => (this.isOnline = true);
    this.offlineHandler = () => (this.isOnline = false);

    window.addEventListener('online', this.onlineHandler);
    window.addEventListener('offline', this.offlineHandler);

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        this.restoreTimerFromStorage();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // every 5 minutes, restore time from storage to ensure timer is accurate
    this.syncInterval = setInterval(() => {
      this.restoreTimerFromStorage();
    }, 5 * MINUTE_IN_SECONDS * TIMER_INTERVAL); // 5 minutes
  },

  ionViewDidEnter() {
    this.restoreTimerFromStorage();
  },

  methods: {
    saveTimerToStorage() {
      if (!this.start) {
        localStorage.removeItem(TIMER_STORAGE_KEY);
        return;
      }

      setItemSafe(
        TIMER_STORAGE_KEY,
        JSON.stringify({ ts: this.start.valueOf() }),
      );
    },

    restoreTimerFromStorage() {
      const storedData = localStorage.getItem(TIMER_STORAGE_KEY);
      if (!storedData) return;

      try {
        const { ts } = JSON.parse(storedData);
        const startTime = dayjs(ts);
        const secondsElapsed = dayjs().diff(startTime, 'second');

        // Check if the timer started within the last 24 hours
        const isWithinADay =
          secondsElapsed > 0 && secondsElapsed < 24 * HOUR_IN_SECONDS;

        if (!isWithinADay) {
          // if more than 24 hours have passed, clear the stored timer
          this.resetTimer();
          return;
        }

        // if timer is valid, restore it
        this.start = startTime;
        this.timeElapsed = secondsElapsed;
        this.startInterval();
      } catch (error) {
        Sentry.captureException(error);
        this.resetTimer();
      }
    },

    async loadExistingTimers() {
      this.existingTimersLoading = true;

      try {
        const today = dayjs().format('YYYY-MM-DD');
        const config = await getParams();
        config.params = { day_only: true };

        const response = await axios.get(
          `/driver/time-report/${today}`,
          config,
        );

        // Response is structured with date as key: { "2026-06-11": { ... } }
        const reportForToday = response.data[today];

        if (
          reportForToday &&
          reportForToday.timer_hours_details &&
          reportForToday.timer_hours_details.agents
        ) {
          this.existingTimers = reportForToday.timer_hours_details.agents;
          this.hoursTotal = reportForToday.hours_agent || 0;
        }
      } catch (error) {
        Sentry.captureException(error);

        this.existingTimers = [];
        this.hoursTotal = 0;
      } finally {
        this.existingTimersLoading = false;
      }
    },

    stopTimer() {
      if (!this.start || !this.timeElapsed) {
        this.resetTimer();
        return;
      }

      // get rounded start first
      const startHour =
        this.start.hour() +
        this.start.minute() / MINUTE_IN_SECONDS +
        this.start.second() / HOUR_IN_SECONDS;
      const roundedStart = this.roundAndLimit(startHour);

      // then, get rounded duration
      const elapsedHours = this.timeElapsed / HOUR_IN_SECONDS;
      let roundedDuration = this.roundAndLimit(elapsedHours);

      // always ensure minimum duration of 5 minutes
      const isTooShort = this.timeElapsed < 5 * MINUTE_IN_SECONDS;
      if (isTooShort) {
        roundedDuration = parseFloat(
          (5 / MINUTE_IN_SECONDS).toFixed(PRECISION),
        );
      }

      // calculate rounded end time from start and duration
      const roundedEnd = roundedStart + roundedDuration;

      const newEntry = {
        day: roundedDuration,
        dayStart: roundedStart,
        dayEnd: parseFloat(roundedEnd.toFixed(PRECISION)),
      };

      this.showConfirmationAlert(newEntry, isTooShort);
    },

    async showConfirmationAlert(newEntry, isTooShort = false) {
      let message = `Êtes-vous sûr de vouloir arrêter le suivi de journée ?`;

      const buttons = [
        {
          text: 'Continuer',
          role: 'cancel',
        },
      ];

      if (isTooShort) {
        message += `
          \n\n
          La durée minimale d'enregistrement est de 5 minutes.
          Si vous confirmez, la durée sera ajustée à 5 minutes.
        `;

        buttons.push({
          text: 'Remettre à zéro',
          handler: () => {
            this.resetTimer();
          },
        });
      }

      buttons.push({
        text: 'Enregistrer',
        role: 'confirm',
        handler: () => {
          this.saveTimerEntry(newEntry);
        },
      });

      const alert = await alertController.create({
        header: 'Arrêter le suivi',
        message: message,
        buttons: buttons,
      });

      await alert.present();
    },

    async saveTimerEntry(newEntry) {
      this.isSaving = true;

      try {
        // If the end time exceeds midnight (24h), subtract 24 to show correct time on next day
        if (newEntry.dayEnd >= 24) {
          newEntry.dayEnd -= 24;
        }

        const start = this.start.format('YYYY-MM-DD');
        const config = await getParams();

        // Prepare updated data to send
        const updatedTimers = [...this.existingTimers, newEntry];
        const updatedTotal = this.hoursTotal + newEntry.day;

        // Prepare minimal data for sending
        const dataToSend = new FormData();
        const reportData = {
          entries: [
            {
              date: start,
              hours_agent: updatedTotal,
              timer_hours_details: {
                agents: updatedTimers,
              },
            },
          ],
        };

        dataToSend.append('report_data', JSON.stringify(reportData));

        // Send the timer entry (special route to ensure we update only needed fields)
        await axios.post('/driver/time-report/timer-hours', dataToSend, config);

        // Success: update local state
        this.existingTimers = updatedTimers;
        this.hoursTotal = updatedTotal;

        const toast = await toastController.create({
          message: 'Période de travail enregistrée.',
          duration: 2000,
          color: 'success',
          position: 'top',
        });
        await toast.present();

        this.resetTimer();
      } catch (error) {
        Sentry.captureException(error);

        const toast = await toastController.create({
          message:
            "Impossible d'enregistrer la période de travail. Veuillez réessayer.",
          duration: 4000,
          color: 'danger',
          position: 'top',
        });

        await toast.present();
      } finally {
        this.isSaving = false;
      }
    },

    /** UTILS METHODS */

    toggleTimer() {
      if (this.start) {
        this.stopTimer();
        return;
      }

      this.startTimer();
    },

    startTimer() {
      this.start = dayjs();
      this.timeElapsed = 0;
      this.saveTimerToStorage();

      this.startInterval();
    },

    resetTimer() {
      this.stopInterval();

      this.start = null;
      this.timeElapsed = 0;
      localStorage.removeItem(TIMER_STORAGE_KEY);
    },

    startInterval() {
      this.stopInterval();

      this.timerInterval = setInterval(() => {
        this.timeElapsed = dayjs().diff(this.start, 'second');
      }, TIMER_INTERVAL);
    },

    stopInterval() {
      if (!this.timerInterval) return;

      clearInterval(this.timerInterval);
      this.timerInterval = null;
    },

    // Round all values to nearest 5 minutes (convert to minutes, round, convert back to hours)
    roundAndLimit(hours) {
      const rounded =
        roundToFiveMinutes(hours * MINUTE_IN_SECONDS) / MINUTE_IN_SECONDS;

      return parseFloat(rounded.toFixed(PRECISION));
    },
  },

  beforeUnmount() {
    this.stopInterval();
    clearInterval(this.syncInterval);
    this.syncInterval = null;

    // Save the current timer state before unmounting
    this.saveTimerToStorage();

    // Clean up event listeners
    window.removeEventListener('online', this.onlineHandler);
    window.removeEventListener('offline', this.offlineHandler);
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  },
};
</script>

<style scoped lang="scss">
.card-header {
  align-items: center;
  margin-bottom: 4px;

  ion-icon {
    margin-left: 2px;
    font-size: 16px;
    cursor: pointer;
  }
}

.start-stop-btn {
  --background: #ffe08d;
  --color: #333;
  --border-radius: 4px;
  text-transform: none;
  height: 60px;
  width: 80px;

  &.active {
    --background: #ff6b6b;
    --color: white;
  }

  ion-icon {
    font-size: 18px;
  }
}

.timer-display {
  flex: 1;
  margin-left: 12px;
  text-align: center;

  .timer {
    font-size: 20px;
    color: black;
  }

  .start-time {
    font-size: 15px;
    color: #999;
  }
}

.toggle-btn {
  --color: #666;
  --padding-start: 0;
  text-transform: none;

  span {
    margin-left: 4px;
  }
}

.timers-list {
  padding: 8px 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .timer-item {
    display: flex;
    justify-content: space-between;
    padding: 6px 8px;
    background-color: #f9f9f9;
    border-radius: 4px;

    .timer-duration {
      color: black;
    }
  }
}
</style>
